import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { animate, useInView } from "framer-motion";
import { useLanguage } from "../../i18n/LanguageContext";
import { siteConfig } from "../../config/site";
import { images } from "../../config/images";
import { Reveal, Stagger, StaggerItem } from "../Reveal";
import { ICON_3D, Icon3D } from "../Icon3D";

const CountUp = ({ to, className }: { to: number; className?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 1.3, ease: [0.22, 1, 0.36, 1], onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref} className={className}>{val}</span>;
};

export const TrustCards = () => {
  const { t } = useLanguage();

  return (
    <section className="section" data-testid="trust-section">
      <div className="container-x">
        <Reveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="eyebrow">{t.trust.eyebrow}</p>
            <h2 className="h2 mt-4">{t.trust.title}</h2>
          </div>
          <span className="inline-flex items-center gap-2 self-start rounded-full border border-charcoal/15 px-4 py-2 text-sm font-medium md:self-auto" data-testid="trust-price-pill">
            <span className="h-1.5 w-1.5 rounded-full bg-clay" /> {t.common.from} {siteConfig.price.perBedPerNight} {siteConfig.price.currency} · {t.common.perBedNight}
          </span>
        </Reveal>

        <Stagger className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 lg:auto-rows-[minmax(0,1fr)]">
          {/* Großes Foto */}
          <StaggerItem className="group relative col-span-2 min-h-[300px] overflow-hidden rounded-2xl lg:row-span-2 lg:min-h-[440px]">
            <img src={images.roomReal} alt="Zimmer der Kola Pension mit mehreren Einzelbetten" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]" loading="lazy" />
            <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6">
              <div>
                <p className="font-serif text-base italic text-cream/80">{t.roomsOverview.eyebrow}</p>
                <p className="mt-1 font-display text-2xl font-medium text-cream" data-testid="trust-photo-title">{t.trust.items[1].title}</p>
              </div>
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-cream/15 text-cream backdrop-blur-md transition-colors duration-300 group-hover:bg-cream group-hover:text-charcoal">
                <ArrowUpRight className="h-5 w-5" aria-hidden />
              </span>
            </div>
          </StaggerItem>

          {/* Stat: Einzelbetten */}
          <StaggerItem className="card group flex flex-col justify-between p-6 transition-shadow duration-300 hover:shadow-lg">
            <Icon3D src={ICON_3D.bed} size="sm" />
            <div className="mt-8">
              <p className="font-display text-5xl font-medium leading-none tracking-tight lg:text-6xl" data-testid="trust-stat-beds"><CountUp to={siteConfig.stats.beds} /></p>
              <p className="mt-2 text-sm text-charcoal-500">{t.common.singleBeds}</p>
            </div>
          </StaggerItem>

          {/* Stat: Zimmer */}
          <StaggerItem className="card group flex flex-col justify-between p-6 transition-shadow duration-300 hover:shadow-lg">
            <Icon3D src={ICON_3D.rooms} size="sm" />
            <div className="mt-8">
              <p className="font-display text-5xl font-medium leading-none tracking-tight lg:text-6xl" data-testid="trust-stat-rooms"><CountUp to={siteConfig.stats.rooms} /></p>
              <p className="mt-2 text-sm text-charcoal-500">{t.nav.rooms}</p>
            </div>
          </StaggerItem>

          {/* Feature: Küche */}
          <StaggerItem className="card group flex flex-col gap-4 p-6 transition-shadow duration-300 hover:shadow-lg">
            <Icon3D src={ICON_3D.kitchen} />
            <div>
              <h3 className="font-display text-lg font-medium" data-testid="trust-feature-kitchen">{t.trust.items[2].title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-charcoal-500">{t.trust.items[2].text}</p>
            </div>
          </StaggerItem>

          {/* Feature: WLAN */}
          <StaggerItem className="card group flex flex-col gap-4 p-6 transition-shadow duration-300 hover:shadow-lg">
            <Icon3D src={ICON_3D.wifi} />
            <div>
              <h3 className="font-display text-lg font-medium" data-testid="trust-feature-wifi">{t.trust.items[3].title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-charcoal-500">{t.trust.items[3].text}</p>
            </div>
          </StaggerItem>
        </Stagger>
      </div>
    </section>
  );
};
